import { genericMemo } from "@redotech/react-util/component";
import { useSearch } from "@redotech/react-util/search";
import { TextMatchExpression } from "@redotech/redo-model/advanced-flow/expressions/text-expression";
import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { DeepPartial } from "@redotech/util/type";
import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";
import { useDelayedAutoFocus } from "../common/use-delayed-auto-focus";

const searcher = new Fuse([], { keys: ["value"], threshold: 0.3 });

export const EnumInspector = genericMemo(function EnumInspector({
  schema,
  dataType,
  expression,
  setExpression,
  showValidationErrors,
}: {
  schema: Schema;
  dataType: "Enum";
  expression: DeepPartial<TextMatchExpression>;
  setExpression: (expression: DeepPartial<TextMatchExpression>) => void;
  showValidationErrors?: boolean;
}) {
  useEffect(() => {
    if (!expression.type) {
      setExpression({ ...expression, type: "text_match", operator: "equals" });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression]);

  const allOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!expression.field) {
      return [];
    }
    const field = schema.fields[expression.field];
    if (field?.dataType !== "Enum" && field?.dataType !== "Multiple Enum") {
      return [];
    }
    return (field.values ?? []).map((value) => ({
      id: value,
      value,
      type: "badge",
      badge: {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        text: schema.fields[expression.field!].prettyValues?.[value] ?? value,
      },
    }));
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema.fields]);

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!expression.matchValues) {
      return [];
    }
    return allOptions.filter((option) =>
      expression.matchValues?.includes(option.value),
    );
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression.matchValues]);

  const [searchText, setSearchText] = useState<string>("");
  const [touched, setTouched] = useState(showValidationErrors || false);
  const delayedAutoFocus = useDelayedAutoFocus(!expression.matchValues?.length);

  const searchResults = useSearch(searcher, allOptions, searchText);

  const errorText = useMemo(() => {
    if (
      (!expression.matchValues || expression.matchValues.length === 0) &&
      touched
    ) {
      return "At least one value is required";
    }
    return undefined;
  }, [expression.matchValues, touched]);

  return (
    <RedoTagsDropdownInput<string>
      autoFocus={delayedAutoFocus}
      description={errorText}
      key={delayedAutoFocus.toString()}
      label="Value"
      onBlur={() => setTouched(true)}
      options={searchResults}
      searchString={searchText}
      selectedOptions={selectedOptions}
      setSearchString={setSearchText}
      setSelectedOptions={(selectedOptions) => {
        setExpression({
          ...expression,
          matchValues: selectedOptions.map((option) => option.value),
        });
      }}
      size="xs"
      state={errorText ? "error" : undefined}
    />
  );
});
